import { I18n } from '@lingui/core';
import { t } from '@lingui/macro';

/**
 * Traduce el `code` de `RequestGateMovementPayload` / `StopGateOperationPayload` (AMARU-862/US-003)
 * a lo que la UI puede **afirmar** frente al operario. La distinción que importa no es
 * éxito/fracaso, sino si sabemos o no qué hizo la compuerta:
 *
 * - `accepted` — el equipo tomó el comando. Hay movimiento (o detención) en curso.
 * - `noop` — éxito sin movimiento: la compuerta ya estaba donde se le pidió (GO03).
 * - `rejected` — **se puede afirmar que el comando no se aplicó**: o el firmware contestó que no
 *   (GO09/GO1x), o el comando nunca salió (entorno no productivo, compuerta no habilitada, posición
 *   previa desconocida). Lo que eso significa para la compuerta depende del comando: un move
 *   rechazado deja la compuerta quieta, un stop rechazado la deja **como estaba**, que puede ser en
 *   movimiento.
 * - `unconfirmed` — **no sabemos**. El comando pudo haber llegado al equipo y la compuerta puede
 *   estar moviéndose ahora mismo; lo que falló fue la confirmación. Un timeout o un corte de
 *   conexión caen acá, y decirle al operario «no se movió» sería mentirle.
 * - `queued` — la solicitud quedó (o ya había una) **en cola** a la espera de que el equipo se
 *   conecte (SGMO-14). La compuerta no se movió todavía, pero puede moverse horas después sin otro
 *   aviso: por eso no es un rechazo ni un «no sabemos».
 * - `replayed` — este pedido **repite** uno que ya estaba registrado y cerrado (mismo `rid`,
 *   SGMO-17): SGMO responde con el cierre de aquél —cancelado, vencido o ya entregado— y no hace
 *   nada nuevo. No es una respuesta del firmware a este intento, así que tampoco es un rechazo.
 */
export type GateCommandOutcome =
  | 'accepted'
  | 'noop'
  | 'rejected'
  | 'unconfirmed'
  | 'queued'
  | 'replayed';

/** Comando que originó el resultado: la misma clasificación dice cosas distintas en cada uno. */
export type GateCommand = 'move' | 'stop';

/** Aceptaciones declaradas: GO00/LEGACY_ACCEPTED en move, GO00 en stop (se detuvo). */
const ACCEPTED_CODES = new Set(['GO00', 'LEGACY_ACCEPTED']);

/**
 * Éxito sin cambio: la compuerta ya estaba en la posición solicitada (GO03, move) o no había un
 * movimiento que detener (GO04, stop).
 */
const NOOP_CODES = new Set(['GO03', 'GO04']);

/**
 * Códigos que el backend resuelve **antes** de que el comando llegue al equipo: nada se movió y
 * afirmarlo no es una suposición.
 */
const NOT_DISPATCHED_CODES = new Set([
  'NO_INVOKE',
  'GATE_ID_NOT_ALLOWED',
  'STATUS_POSITION_UNKNOWN',
]);

/**
 * Códigos en los que el comando pudo haber llegado al equipo y lo que se perdió es la respuesta.
 * `CONTRACT_VIOLATION` entra acá justamente porque el equipo **sí** contestó: contestó algo que no
 * se puede leer.
 */
const UNCONFIRMED_CODES = new Set([
  'GATE_COMMAND_TIMEOUT',
  'GATE_COMMAND_CONNECTION_ERROR',
  'CONTRACT_VIOLATION',
]);

/**
 * Éxito del backend que **no** confirma el efecto físico: `LEGACY_OK` es el stop legado, que
 * publica y responde sin esperar al equipo. Se evalúa antes que `success`.
 */
const SENT_WITHOUT_CONFIRMATION_CODES = new Set(['LEGACY_OK']);

/**
 * Solicitudes en cola (SGMO-14): `QUEUED` es la que se acaba de encolar porque el equipo no
 * respondió a tiempo; `ALREADY_QUEUED`, la nueva que no se registró porque ya había una pendiente.
 * En los dos casos hay una solicitud viva que puede mover la compuerta más tarde.
 */
const QUEUED_CODES = new Set(['QUEUED', 'ALREADY_QUEUED']);

/**
 * Cierres del carril encolado con los que SGMO responde a un pedido que repite un `rid` ya cerrado
 * (colisión de siembra de SGMO-17). Van antes que `PROTOCOL_CODE` por claridad, aunque el prefijo
 * `Q` ya no calza con él.
 */
const REPLAYED_CODES = new Set(['QCANCEL', 'QEXPIRED', 'QDUPMID']);

/** Código del protocolo del firmware (GO09, GO10-GO19, GO1A, GO2x...): el equipo contestó «no». */
const PROTOCOL_CODE = /^GO[0-9A-Z]{2}$/;

interface GateCommandResultLike {
  success: boolean;
  code: string;
}

export const classifyGateCommandOutcome = ({
  success,
  code,
}: GateCommandResultLike): GateCommandOutcome => {
  if (NOOP_CODES.has(code)) return 'noop';
  if (QUEUED_CODES.has(code)) return 'queued';
  if (REPLAYED_CODES.has(code)) return 'replayed';
  if (SENT_WITHOUT_CONFIRMATION_CODES.has(code)) return 'unconfirmed';
  if (success || ACCEPTED_CODES.has(code)) return 'accepted';
  if (NOT_DISPATCHED_CODES.has(code)) return 'rejected';
  if (UNCONFIRMED_CODES.has(code)) return 'unconfirmed';
  if (PROTOCOL_CODE.test(code)) return 'rejected';
  // Código que esta versión del frontend no conoce: no se afirma nada sobre la compuerta. El
  // default conservador es el que no puede mentir.
  return 'unconfirmed';
};

export type GateCommandSeverity = 'success' | 'warning' | 'error';

export const severityForOutcome = (outcome: GateCommandOutcome): GateCommandSeverity => {
  switch (outcome) {
    case 'accepted':
    case 'noop':
      return 'success';
    case 'unconfirmed':
    case 'queued':
    case 'replayed':
      return 'warning';
    case 'rejected':
      return 'error';
  }
};

/**
 * Frase que la UI agrega al mensaje que ya redactó el backend. El backend explica *qué pasó*; esto
 * dice *qué puede dar por cierto el operario*, que es la parte que no se puede deducir del texto.
 */
export const detailForOutcome = (
  outcome: GateCommandOutcome,
  i18n: I18n,
  command: GateCommand,
): string | null => {
  switch (outcome) {
    case 'accepted':
      return null;
    case 'replayed':
      // El mensaje del backend ya dice qué pasó con la solicitud original.
      return null;
    case 'noop':
      // En stop, el mensaje del backend (GO04) ya dice lo único que hay que saber.
      return command === 'stop'
        ? null
        : t(i18n)`No se inició una operación porque no hacía falta mover la compuerta.`;
    case 'rejected':
      return command === 'stop'
        ? t(i18n)`La detención no se aplicó: si la compuerta estaba en movimiento, sigue moviéndose.`
        : t(i18n)`La compuerta no se movió.`;
    case 'unconfirmed':
      return t(
        i18n,
      )`No se pudo confirmar el resultado: la compuerta podría estar moviéndose. Verifique su estado antes de reintentar.`;
    case 'queued':
      return t(
        i18n,
      )`Mientras siga en cola, la compuerta puede moverse más tarde sin otro aviso. Para cancelarla, detenga la operación.`;
  }
};

/**
 * Mensaje final para el snackbar: el texto del backend (fuente de verdad del caso) más el detalle
 * de UI. Si el backend no mandó mensaje, el detalle solo ya dice lo esencial; si tampoco hay
 * detalle, se cae al código para no dejar al operario sin nada que reportar.
 */
export const composeGateCommandMessage = (
  { message, code }: { message?: string | null; code: string },
  outcome: GateCommandOutcome,
  i18n: I18n,
  command: GateCommand,
): string => {
  const detail = detailForOutcome(outcome, i18n, command);
  const parts = [message?.trim(), detail].filter((part): part is string => Boolean(part));
  if (parts.length === 0) return t(i18n)`La compuerta respondió con el código ${code}.`;
  return parts.join(' ');
};
